import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Plane, TrainFront, BedDouble, LogIn, ArrowRight } from "lucide-react";
import { useTrip, useUI } from "@/lib/store";
import { TRIP_DATES, TRIP_START, fmtLong, dayNum, hotelForNight, daysBetween } from "@/lib/dates";
import { EventCard } from "@/components/EventCard";
import { TransportCard } from "@/components/TransportCard";
import { CalendarButton } from "@/components/CalendarButton";

const OCT_DAYS = Array.from({ length: 31 }, (_, i) => `2026-10-${String(i + 1).padStart(2, "0")}`);
const LEAD = 3; // 1 Oct 2026 is a Thursday (Mon-first grid)
const DOW = ["M", "T", "W", "T", "F", "S", "S"];
const inTrip = (d) => TRIP_DATES.includes(d);

export default function Calendar() {
  const { trip, today, organiser } = useTrip();
  const { openEditor, showDriver } = useUI();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [view, setView] = useState(params.get("view") === "agenda" ? "agenda" : "month");
  const [sel, setSel] = useState(params.get("date") || (TRIP_DATES.includes(today) ? today : null));

  const itemsFor = useMemo(() => {
    const map = {};
    TRIP_DATES.forEach((d) => {
      map[d] = [
        ...trip.transports.filter((t) => t.date === d).map((t) => ({ kind: "transport", Icon: t.type === "flight" ? Plane : TrainFront, label: t.title, time: t.departTime })),
        ...trip.hotels.filter((h) => h.checkIn === d).map((h) => ({ kind: "hotel", Icon: BedDouble, label: h.name, time: null })),
        ...trip.events.filter((e) => e.date === d && (!e.private || organiser)).map((e) => ({ kind: "event", label: e.title, time: e.startTime, completed: e.completed })),
      ];
    });
    return map;
  }, [trip, organiser]);

  return (
    <div className="pb-8">
      <div className="mt-3 flex items-end justify-between gap-3">
        <div><p className="eyebrow">Trip calendar</p><h1 className="font-display text-3xl font-semibold leading-tight">October 2026</h1></div>
        <button data-testid="calendar-add-btn" onClick={() => openEditor({ mode: "add", date: sel || TRIP_DATES[0] })} className="tap inline-flex items-center gap-1.5 rounded-full bg-[var(--jade)] px-4 py-2.5 text-sm font-semibold text-[var(--parchment)]"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="mt-3 inline-flex rounded-full border bg-white p-1 text-xs font-semibold">
        {[["month", "Month"], ["agenda", "Agenda"]].map(([v, l]) => <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)} className={`tap rounded-full px-4 py-1.5 ${view === v ? "bg-[var(--jade)] text-[var(--parchment)]" : ""}`}>{l}</button>)}
      </div>

      {view === "month" ? (
        <>
          <div data-testid="calendar-month-grid" className="-mx-1 mt-3">
            <div className="grid grid-cols-7 px-1 pb-1">{DOW.map((d, i) => <span key={i} className="text-center text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-muted)]">{d}</span>)}</div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: LEAD }).map((_, i) => <span key={`b${i}`} />)}
              {OCT_DAYS.map((d) => {
                const trip_ = inTrip(d);
                const items = trip_ ? itemsFor[d] || [] : [];
                const day = trip.days.find((x) => x.date === d);
                const active = sel === d;
                const isToday = d === today;
                const label = items[0]?.label;
                const Ic = items[0]?.Icon;
                return (
                  <button key={d} data-testid={`cal-day-${d}`} onClick={() => setSel(d)}
                    className={`tap relative flex min-h-[74px] flex-col items-stretch overflow-hidden rounded-xl border p-1 text-left sm:min-h-[84px] md:min-h-[104px] ${active ? "border-[var(--jade)] bg-[var(--jade)] text-[var(--parchment)]" : trip_ ? (day?.transition ? "border-[var(--coral)]/40 bg-[var(--coral-soft)]/70" : "border-[var(--jade)]/25 bg-[var(--jade-soft)]/70") : "border-transparent bg-white/60 text-[var(--ink-muted)]"}`}>
                    <span className="flex items-center justify-between"><span className={`font-display text-lg font-semibold leading-none ${isToday ? "rounded-full bg-[var(--coral)] px-1.5 text-white" : ""}`}>{dayNum(d)}</span>{items.length > 0 && <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[var(--coral)]" : "bg-[var(--brass)]"}`} />}</span>
                    {d === TRIP_START && <span className={`mt-1 text-[9px] font-bold uppercase tracking-wider ${active ? "text-[var(--brass)]" : "text-[var(--jade)]"}`}>Trip begins</span>}
                    {trip_ && label && <span className={`mt-1 line-clamp-2 text-[10px] leading-tight ${active ? "text-[var(--parchment)]/85" : "text-[var(--ink)]"}`}>{Ic ? <Ic className="mr-0.5 inline h-2.5 w-2.5" /> : null}{label}</span>}
                    {trip_ && items.length > 1 && <span className={`mt-auto text-[9px] ${active ? "text-[var(--parchment)]/70" : "text-[var(--ink-muted)]"}`}>+{items.length - 1} more</span>}
                    {trip_ && !items.length && <span className={`mt-auto truncate text-[9px] uppercase tracking-wider ${active ? "text-[var(--parchment)]/70" : "text-[var(--ink-muted)]"}`}>{day?.city.split(" ")[0]}</span>}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-3 px-1 text-[10px] text-[var(--ink-muted)]"><span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[var(--jade-soft)] border border-[var(--jade)]/25" /> China trip 20–29 Oct</span><span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[var(--coral-soft)] border border-[var(--coral)]/40" /> Travel day</span><span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[var(--brass)]" /> Has plans</span></div>
          </div>
          <AnimatePresence mode="wait">
            {sel ? <motion.div key={sel} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-4">{inTrip(sel) ? <DayAgenda date={sel} itemsFor={itemsFor} /> : <OutsideDay date={sel} />}</motion.div>
              : <p className="mt-4 text-center text-sm text-[var(--ink-muted)]">Tap a date to see its agenda.</p>}
          </AnimatePresence>
        </>
      ) : (
        <div data-testid="calendar-agenda" className="mt-4 space-y-6">
          {TRIP_DATES.map((d) => <DayAgenda key={d} date={d} itemsFor={itemsFor} compact />)}
        </div>
      )}
    </div>
  );
}

function OutsideDay({ date }) {
  const before = date < TRIP_START;
  const n = before ? daysBetween(date, TRIP_START) : daysBetween("2026-10-29", date);
  return (
    <section data-testid={`outside-${date}`} className="card-soft p-4">
      <p className="eyebrow">{before ? "Before the trip" : "After the trip"}</p>
      <h2 className="font-display text-2xl font-semibold leading-tight">{fmtLong(date)}</h2>
      <p className="mt-1 text-sm text-[var(--ink-muted)]">{before ? `China is in ${n} day${n === 1 ? "" : "s"}. Preparation reminders can be added to any trip day from the Add button.` : `${n} day${n === 1 ? "" : "s"} since flying home.`}</p>
    </section>
  );
}

function DayAgenda({ date, compact }) {
  const { trip, organiser } = useTrip();
  const { openEditor, showDriver } = useUI();
  const navigate = useNavigate();
  const day = trip.days.find((d) => d.date === date);
  const transports = trip.transports.filter((t) => t.date === date);
  const checkIn = trip.hotels.find((h) => h.checkIn === date);
  const events = trip.events.filter((e) => e.date === date && (!e.private || organiser));
  const tonight = hotelForNight(trip.hotels, date);
  return (
    <section data-testid={`agenda-${date}`}>
      <div className="mb-2 flex items-end justify-between">
        <div><p className="eyebrow">{day?.city}{day?.transition ? ` · travel day` : ""}</p><h2 className="font-display text-2xl font-semibold leading-tight">{fmtLong(date)}</h2></div>
        <div className="flex gap-2">
          <button data-testid={`agenda-add-${date}`} onClick={() => openEditor({ mode: "add", date })} className="tap inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> Add</button>
          {!compact && <CalendarButton kind="day" item={date} label="Export" compact testId={`agenda-export-${date}`} />}
        </div>
      </div>
      <div className="space-y-2">
        {transports.map((t) => <TransportCard key={t.id} t={t} compact />)}
        {checkIn && (
          <div className="card-soft flex items-center gap-3 p-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--jade-soft)] text-[var(--jade)]"><LogIn className="h-4 w-4" /></span>
            <div className="flex-1 text-sm"><p className="font-semibold">Check in · {checkIn.name}</p><p className="font-cn text-xs text-[var(--ink-muted)]">{checkIn.addressChinese}</p></div>
            <button data-testid={`agenda-driver-${checkIn.id}`} onClick={() => showDriver(checkIn)} className="tap rounded-full bg-[var(--jade)] px-3 py-1.5 text-xs font-semibold text-[var(--parchment)]">Driver</button>
          </div>
        )}
        {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
        {!events.length && !transports.length && !checkIn && <p className="rounded-2xl border border-dashed p-4 text-center text-sm text-[var(--ink-muted)]">Nothing scheduled — tap Add.</p>}
        {tonight && !compact && <p className="flex items-center gap-2 px-1 text-xs text-[var(--ink-muted)]"><BedDouble className="h-3.5 w-3.5" /> Night at {tonight.name}, {tonight.city}</p>}
        {!compact && <button data-testid={`agenda-open-trip-${date}`} onClick={() => navigate(`/trip?date=${date}`)} className="tap inline-flex items-center gap-1 px-1 text-sm font-semibold text-[var(--jade)]">Open in Trip <ArrowRight className="h-4 w-4" /></button>}
      </div>
    </section>
  );
}
